import './reward.css';
import { t } from './i18n';
import type { CoinEvent } from '../net/protocol';

/**
 * The 코인 payout, shown the moment it lands.
 *
 * The faucet only changes behaviour if the player sees it pay — an invisible
 * balance bump teaches nothing. Each award is itemized so the reason is legible
 * ("new high run", "first win of the day"), which is what makes the next one
 * feel worth chasing.
 */
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

const LABEL: Record<CoinEvent['kind'], (n: number) => string> = {
  innings: (n) => t('earn.innings', { n }),
  dailyWin: () => t('earn.dailyWin'),
  dailyClear: () => t('earn.dailyClear'),
  dailyBest: () => t('earn.dailyBest'),
};

const HOLD_MS = 5200;

export class RewardToast {
  private readonly root = el('div', 'reward-toast');
  private readonly list = el('div', 'reward-list');
  private readonly total = el('div', 'reward-total');
  private timer: number | null = null;

  constructor(root: HTMLElement) {
    const title = el('div', 'reward-title');
    title.textContent = t('earn.title');
    this.root.append(title, this.list, this.total);
    root.appendChild(this.root);
  }

  /** Show one payout. `balance` is the resulting 코인 total. */
  show(events: CoinEvent[], balance: number): void {
    const paid = events.filter((e) => e.coins > 0);
    if (!paid.length) return;

    this.list.replaceChildren();
    for (const e of paid) {
      const row = el('div', 'reward-row');
      const what = el('span', 'reward-what');
      what.textContent = LABEL[e.kind]?.(e.n ?? 0) ?? e.kind;
      const coins = el('span', 'reward-coins');
      coins.textContent = `+${e.coins}`;
      if (e.kind === 'dailyWin') row.classList.add('is-big');
      row.append(what, coins);
      this.list.appendChild(row);
    }
    this.total.textContent = t('store.balance', { n: balance });

    this.root.classList.add('open');
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.root.classList.remove('open'), HOLD_MS);
  }
}
